import { ReactNode } from "react";
import { Row } from "react-bootstrap";
import { FaHome } from "react-icons/fa";
import CustomModal from "shared/components/modal/CustomModal";
import FormSelectAsync from "shared/components/select/formSelectAsync/FormSelectAsync";
import {
  BlocoSelectDTO,
  CondominioSelectDTO,
  UnidadeSelecaoOpcaoDTO,
} from "types/dto/unidade/UnidadeSelectDTO";
import { filtrarOpcaoUnidade } from "./unidadeSelecao";

const SELECT_CONTAINER_STYLE = {
  width: "100%",
  marginLeft: 0,
};

const SELECT_FIELD_CLASS = "condominio-select-field";

const pessoaStyleNormal = {
  fontSize: "13px",
  color: "black",
};

const pessoaStyle = {
  ...pessoaStyleNormal,
  fontWeight: "bold" as const,
};

const contatoStyle = {
  fontSize: "12px",
  color: "black",
};

const tagStyleProprietario = {
  marginLeft: "20px",
  borderRadius: "5px",
  display: "inline-flex" as const,
  justifyContent: "center" as const,
  alignItems: "center" as const,
  width: "90px",
  height: "2vh",
  fontSize: "11px",
  color: "white",
  background: "#2f4050",
};

const tagStyleInquilino = {
  ...tagStyleProprietario,
  background: "orange",
};

interface UnidadeSelectModalProps {
  show: boolean;
  onHide: () => void;
  selectedCondominio: CondominioSelectDTO;
  onCondominioChange: (value: CondominioSelectDTO | null) => void;
  selectedBloco: BlocoSelectDTO;
  onBlocoChange: (value: BlocoSelectDTO | null) => void;
  selectedUnidade: UnidadeSelecaoOpcaoDTO;
  onUnidadeChange: (value: UnidadeSelecaoOpcaoDTO | null) => void;
  onFooterLinkClick: () => void;
  loading?: boolean;
  title?: string;
}

/**
 * As opções chegam prontas do backend (UnidadeSelecaoOpcaoFactory): este modal só exibe.
 * No menu, rótulo em negrito + papel; como valor selecionado, o rótulo completo.
 */
const formatarOpcaoUnidade = (
  opcao: UnidadeSelecaoOpcaoDTO,
  meta?: { context?: string }
): ReactNode => {
  if (!opcao?.chave) {
    return "";
  }
  if (meta?.context !== "menu") {
    return opcao.rotuloCompleto ?? "";
  }
  return (
    <span>
      <strong>{opcao.rotulo}</strong>
      {opcao.rotuloPapel ? ` - ${opcao.rotuloPapel}` : ""}
    </span>
  );
};

const renderUnidadeFooter = (opcao: UnidadeSelecaoOpcaoDTO, onFooterLinkClick: () => void) => {
  if (!opcao?.id) {
    return null;
  }

  const isInquilino = opcao.papel === "inquilino";

  return (
    <div className="condominio-select-unidade-footer">
      <div id="footerImage" className="condominio-select-unidade-footer-image">
        <FaHome />
      </div>
      <div id="footerContent" className="condominio-select-unidade-footer-content">
        <span id="pessoaSearched" style={pessoaStyle}>
          {opcao.rotulo}
          {opcao.rotuloPapel && (
            <span id="tagIdentificacao" style={isInquilino ? tagStyleInquilino : tagStyleProprietario}>
              {opcao.rotuloPapel}
            </span>
          )}
        </span>
        <span id="pessoaInformacao" style={contatoStyle}>
          {opcao.contato}
        </span>
        <span id="proprietarioInquilino" style={pessoaStyleNormal}>
          {opcao.outroPapelRotulo ? (
            <span>
              <strong>{opcao.outroPapelRotulo}</strong>
              {`: ${opcao.outroPapelNome}`}
            </span>
          ) : (
            ""
          )}
        </span>
      </div>
      <div
        id="footerLink"
        className="condominio-select-unidade-footer-link"
        onClick={(event) => {
          event.preventDefault();
          onFooterLinkClick();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && onFooterLinkClick) {
            event.preventDefault();
            onFooterLinkClick();
          }
        }}
      >
        Ver unidade &gt;
      </div>
    </div>
  );
};

const UnidadeSelectModal = (props: UnidadeSelectModalProps) => {
  const hasFooter = !!props.selectedUnidade?.id;
  const hasCondominioSelected = !!props.selectedCondominio?.id;

  return (
    <CustomModal
      titulo={props.title || "Buscar por unidade"}
      showModal={props.show}
      size="sm"
      tipoModal="MODAL_CENTRAL"
      usarSize={false}
      dialogClassName="condominio-select-modal"
      fecharModalAoClicarFora
      onCloseModal={props.onHide}
      bodyClassName="custom-modal-body-medium"
      customFooter={hasFooter ? renderUnidadeFooter(props.selectedUnidade, props.onFooterLinkClick) : null}
    >
      <Row>
        {props.show && (
          <FormSelectAsync<CondominioSelectDTO>
            id="condominioSelect"
            label="Condomínio"
            className={SELECT_FIELD_CLASS}
            selectContainerStyle={SELECT_CONTAINER_STYLE}
            url="condominio"
            valueKey="id"
            valueLabel="nomeCondominio"
            value={props.selectedCondominio?.id ? props.selectedCondominio : null}
            visualizacao={props.selectedCondominio?.nomeCondominio}
            onChange={(value) => props.onCondominioChange(value)}
            disabled={props.loading}
            isLazy
            noPage
            sort="pessoaJuridica.nomeFantasia"
            parametros={{
              considerarTambemSemPool: true,
              situacao: "ATIVA",
            }}
          />
        )}
        {props.show && (
          <FormSelectAsync<BlocoSelectDTO>
            key={`blocoSelect-${props.selectedCondominio?.id || "empty"}`}
            id="blocoSelect"
            label="Bloco"
            className={SELECT_FIELD_CLASS}
            selectContainerStyle={SELECT_CONTAINER_STYLE}
            url="bloco/all"
            valueKey="id"
            valueLabel="nomeBloco"
            value={props.selectedBloco?.id ? props.selectedBloco : null}
            visualizacao={props.selectedBloco?.nomeBloco}
            onChange={(value) => props.onBlocoChange(value)}
            disabled={props.loading || !hasCondominioSelected}
            isLazy
            noPage
            parametros={{
              ...(props.selectedCondominio?.id && { condominioId: props.selectedCondominio.id }),
            }}
          />
        )}
        {props.show && (
          <FormSelectAsync<UnidadeSelecaoOpcaoDTO>
            key={`unidadeSelect-${props.selectedCondominio?.id || "all"}-${props.selectedBloco?.id || "all"}`}
            id="unidadeSelect"
            label="Unidade"
            className={SELECT_FIELD_CLASS}
            selectContainerStyle={SELECT_CONTAINER_STYLE}
            url="unidade/unidades/all"
            valueKey="chave"
            valueLabel="rotuloCompleto"
            value={props.selectedUnidade?.chave ? props.selectedUnidade : null}
            placeholder="Buscar unidade pelo seu nome, nome do proprietário ou do inquilino"
            placeholderFontSize="15px"
            onChange={(value) => props.onUnidadeChange(value)}
            disabled={props.loading}
            isLazy
            noPage
            autoLoadWhenReady
            canLoadOnMenuOpen={() => true}
            formatOptionLabel={formatarOpcaoUnidade}
            filterOption={filtrarOpcaoUnidade}
            parametros={{
              ...(props.selectedCondominio?.id && { condominioId: props.selectedCondominio.id }),
              ...(props.selectedBloco?.id && { blocoId: props.selectedBloco.id }),
            }}
          />
        )}
      </Row>
    </CustomModal>
  );
};

export default UnidadeSelectModal;
